import React, { useEffect, useRef, useCallback } from 'react';
class CancelError extends Error {
    constructor(message) {
        super(message);
        this.name = 'CancelError';
    }
}
const useCancel = ({ isCancelOnUnmount = true, resolveCancelEffect = undefined, cancelEffectDeps = [], resolveResetCancelEffect = undefined, resetCancelEffectDeps = [], } = {}) => {
    const isCancelled = useRef(false);
    const isUnmounted = useRef(false);
    const currentCancelId = useRef(Symbol('useCancel.currentCancelId.initialCancelId'));
    const cancel = useCallback(() => {
        isCancelled.current = true;
        currentCancelId.current = Symbol('useCancel.currentCancelId.newCancelId');
    }, []);
    const resetCancel = useCallback(() => {
        isCancelled.current = false;
    }, []);
    const getIsAlreadyCancelled = (cancelId) => () => cancelId !== currentCancelId.current;
    const getCurrentIsCancelled = useCallback(({ isTrackAlreadyCancelled = false } = {}) => {
        if (isTrackAlreadyCancelled) {
            const cancelIdInstance = currentCancelId.current;
            return [isCancelled.current, getIsAlreadyCancelled(cancelIdInstance)];
        }
        return isCancelled.current;
    }, []);
    const cancellableUpdate = useCallback((update, { onResult = () => { }, onError = () => { }, cancelErrorMessage = '', isLazy = false, isPendingCancelled = false, } = {}) => (...args) => {
        let isLocalUpdateCancelled = false;
        const usesPendingCancellable = isPendingCancelled !== false &&
            Object.hasOwnProperty.call(isPendingCancelled, 'current');
        const cancelIdInstance = currentCancelId.current;
        const getGlobalCancelTriggered = getIsAlreadyCancelled(cancelIdInstance);
        const asyncCancelTask = () => Promise.resolve('initPromise')
            .then(() => {
            const isAlreadyCancelled = getGlobalCancelTriggered();
            if (isAlreadyCancelled ||
                isLocalUpdateCancelled ||
                (usesPendingCancellable && isPendingCancelled.current) ||
                isUnmounted.current) {
                return Promise.reject(new CancelError(cancelErrorMessage));
            }
            return Promise.resolve(update(...args));
        })
            .catch((error) => Promise.reject(error));
        const runAsyncCancel = () => asyncCancelTask().then(onResult).catch(onError);
        const cancelLocalUpdate = () => {
            isLocalUpdateCancelled = true;
            return isLocalUpdateCancelled;
        };
        const promises = {
            awaitable: runAsyncCancel,
            thennable: asyncCancelTask,
        };
        if (!isLazy) {
            const result = runAsyncCancel();
            return [result, cancelLocalUpdate, promises];
        }
        return [promises, cancelLocalUpdate];
    }, []);
    const createCancellableUpdate = useCallback((update, { onResult = (result) => [null, result], onError = (error) => [error, null], cancelErrorMessage = 'Update cancelled', isLazy = false, isPendingCancellable = false, } = {}) => {
        if (isPendingCancellable) {
            const isPendingCancelled = React.createRef();
            // @ts-expect-error ts-migrate(2540) FIXME: Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
            isPendingCancelled.current = false;
            const cancelPendingUpdates = () => {
                // @ts-expect-error ts-migrate(2540) FIXME: Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
                isPendingCancelled.current = true;
                return isPendingCancelled.current;
            };
            return [
                cancellableUpdate(update, {
                    onResult,
                    onError,
                    cancelErrorMessage,
                    isLazy,
                    isPendingCancelled,
                }),
                cancelPendingUpdates,
            ];
        }
        return cancellableUpdate(update, {
            onResult,
            onError,
            cancelErrorMessage,
            isLazy,
            isPendingCancelled: false,
        });
    }, [cancellableUpdate]);
    useEffect(() => {
        // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
        if (resolveCancelEffect?.(isCancelled.current)) {
            cancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, cancelEffectDeps);
    useEffect(() => {
        // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
        if (resolveResetCancelEffect?.(isCancelled.current)) {
            resetCancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, resetCancelEffectDeps);
    useEffect(() => {
        if (isCancelOnUnmount) {
            return () => {
                isCancelled.current = true;
                isUnmounted.current = true;
            };
        }
        return () => {
            isUnmounted.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return {
        cancel,
        resetCancel,
        getCurrentIsCancelled,
        createCancellableUpdate,
        CancelError,
    };
};
export { useCancel, CancelError };
